import {useMemo} from 'react';
import {kr, tickLabels} from '../../lib/format.js';
import {Defs, fillById, niceScale, Grid, useHover, HitBands, Tooltip, Legend} from './primitives.jsx';

const SERIES = [
	{key: 'revenue', label: 'Nettoomsättning', fill: 'solid'},
	{key: 'direct', label: 'Råvaror och förnödenheter', fill: 'hatch'},
	{key: 'external', label: 'Övriga externa kostnader', fill: 'cross'},
	{key: 'personnel', label: 'Personalkostnader', fill: 'grey'},
	{key: 'depreciation', label: 'Avskrivningar', fill: 'dots'},
	{key: 'other_operating', label: 'Övriga rörelsekostnader', fill: 'open'},
];
const W = 560, H = 250, SH = 78, L = 52, R = 10, T = 10, B = 24;

/**
 * Revenue up, costs down, stacked — then the resulting month result as its own strip
 * beneath, sharing the x positions but NOT the y scale. Two scales in one plot would
 * be the dual-axis mistake; two stacked plots that share only x is the honest way to
 * show "these components produced that result".
 */
export default function Composition({months, per = 'månad'}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(months), [months]);
	const g = useMemo(() => {
		let hi = 0, lo = 0;
		for (const m of months) {
			let up = 0, dn = 0;
			for (const s of SERIES) { const v = m[s.key] ?? 0; v >= 0 ? (up += v) : (dn += v); }
			hi = Math.max(hi, up); lo = Math.min(lo, dn);
		}
		const sc = niceScale(lo, hi);
		const iw = W - L - R, ih = H - T - B;
		const y = v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih;
		const x = i => L + (iw * (i + 0.5)) / months.length;
		const bw = Math.max(2, Math.min(26, (iw / months.length) * 0.62));
		const bars = months.flatMap((m, i) => {
			let up = 0, dn = 0;
			return SERIES.map(s => {
				const v = m[s.key] ?? 0;
				if (!v) return null;
				const h = (Math.abs(v) / (sc.hi - sc.lo)) * ih;
				if (h < 0.4) return null;
				const yy = v > 0 ? y(up + v) : y(dn);
				if (v > 0) up += v; else dn += v;
				return {key: `${m.month}-${s.key}`, x: x(i) - bw / 2, y: yy, w: bw,
					h: Math.max(h - (bw > 6 ? 1 : 0), 0.6), ...fillById(s.fill)};
			}).filter(Boolean);
		});
		// The result strip: its own scale, its own baseline.
		const rs = niceScale(Math.min(0, ...months.map(m => m.result ?? 0)),
			Math.max(0, ...months.map(m => m.result ?? 0)), 2);
		const ry = v => 10 + (SH - 30) - ((v - rs.lo) / (rs.hi - rs.lo)) * (SH - 30);
		return {bars, y, x, sc, bw, rs, ry, ih};
	}, [months]);

	if (!months.length) return null;

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">Intäkter och kostnader per {per}</span>
				<span className="chart__hint">intäkter uppåt, kostnader nedåt. Nedanför: periodens resultat.</span>
			</div>
			<div className="chart__canvas chart__canvas--tall">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label={`Intäkter och kostnader per ${per}`}>
					<Defs />
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{g.bars.map(({key, x: bx, y: by, w, h, ...paint}) => (
						<rect key={key} x={bx} y={by} width={w} height={h} rx={w > 6 ? 2 : 0} {...paint} />
					))}
					{ticks.map((t, i) => (t == null ? null : (
						<text key={months[i].month} x={g.x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={months} x={g.x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={months} at={at} x={g.x} W={W} title={m => m.label ?? m.month} rows={m => [
					['Nettoomsättning', kr(m.revenue)], ['Råvaror', kr(m.direct)],
					['Övriga externa', kr(m.external)], ['Personal', kr(m.personnel)],
					['Avskrivningar', kr(m.depreciation)], ['Rörelseresultat', kr(m.operating_result)],
					['Resultat', `${kr(m.result)} kr`],
				]} />
			</div>
			<div className="chart__striplabel">Beräknat resultat</div>
			<div className="chart__strip">
				<svg viewBox={`0 0 ${W} ${SH}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
					<line x1={L} x2={W - R} y1={g.ry(0)} y2={g.ry(0)} className="zl" />
					<text x={L - 6} y={g.ry(g.rs.hi) + 3.5} className="ax" textAnchor="end">
						{Math.round(g.rs.hi / 1000)}k</text>
					<text x={L - 6} y={g.ry(g.rs.lo) + 3.5} className="ax" textAnchor="end">
						{Math.round(g.rs.lo / 1000)}k</text>
					{months.map((m, i) => {
						const v = m.result ?? 0;
						const h = Math.abs(g.ry(v) - g.ry(0));
						return <rect key={m.month} x={g.x(i) - g.bw / 2} y={v > 0 ? g.ry(v) : g.ry(0)}
							width={g.bw} height={Math.max(h, 0.6)} rx={g.bw > 6 ? 2 : 0}
							fill={m.ledger_partial ? '#d9d9d9' : '#000'} />;
					})}
				</svg>
			</div>
			<Legend items={SERIES.map(s => ({label: s.label, fill: s.fill}))} />
		</div>
	);
}
